/**
 * Customer Referrals — standalone "bring a friend" page.
 *
 * Hero card: large customer code + copy + native-share buttons (WhatsApp,
 * SMS, Email). Stats row + recent referrals list (if any).
 */
import React, { useEffect, useState } from "react";
import { Copy, Check, MessageCircle, Mail, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { SkeletonBlock } from "../../components/Skeleton";

const Card = ({ className = "", testId, children }) => (
  <section
    data-testid={testId}
    className={`bg-napoli-marble border border-napoli-gold/30 p-6 ${className}`}
  >
    {children}
  </section>
);

const CustomerReferralsPage = () => {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get("/customer/referral");
        if (alive) setData(res.data);
      } catch (_) {
        if (alive) setData({ referral_code: "", referrals: [], successful_referrals: 0 });
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const code = data?.referral_code || "";
  const link = data?.referral_link || "";
  const successful = data?.successful_referrals ?? 0;
  const bonus = data?.bonus_points_earned ?? 0;
  const referrals = data?.referrals || [];

  const shareText = link
    ? `Pull up a chair at Napoli — use my code: ${code}\n${link}`
    : `Pull up a chair at Napoli — use my code: ${code}`;

  const copy = async () => {
    const payload = link || code;
    if (!payload) return;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(payload);
      } else {
        const ta = document.createElement("textarea");
        ta.value = payload;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
    } catch (_) { /* optimistic */ }
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2200);
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Napoli Rewards", text: shareText, url: link || undefined });
      } catch (_) { /* user cancelled */ }
    } else {
      copy();
    }
  };

  if (!data) {
    return (
      <div data-testid="referrals-skeleton" className="px-6 md:px-10 py-8 md:py-12 max-w-2xl mx-auto space-y-6">
        <SkeletonBlock className="h-44" />
        <SkeletonBlock className="h-24" />
        <SkeletonBlock className="h-40" />
      </div>
    );
  }

  return (
    <section
      data-testid="customer-referrals-page"
      className="px-6 md:px-10 py-8 md:py-12 max-w-2xl mx-auto"
    >
      <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
        Bring a friend
      </p>
      <h1 className="mt-2 font-display font-bold text-[28px] md:text-[36px] text-napoli-espresso leading-tight">
        Pull up another chair.
      </h1>
      <p className="mt-2 font-body italic text-[13px] text-napoli-espresso-soft">
        They get a welcome plate. You get 100 points the night they show up.
      </p>

      {/* Hero card with code */}
      <Card testId="referrals-hero" className="mt-6">
        <p className="font-body text-[10px] tracking-[0.3em] uppercase text-napoli-espresso-soft">
          Your code
        </p>
        <p
          data-testid="referrals-code"
          className="mt-2 font-mono font-bold text-[28px] md:text-[36px] text-napoli-espresso tracking-[0.1em] break-all"
        >
          {code || "—"}
        </p>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="button"
            data-testid="referrals-copy"
            onClick={copy}
            disabled={!code}
            className="inline-flex items-center gap-2 bg-napoli-oxblood text-napoli-cream px-5 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-oxblood-dark transition-colors disabled:opacity-50"
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? "Copied" : "Copy link"}
          </button>
          <button
            type="button"
            data-testid="referrals-share-native"
            onClick={shareNative}
            disabled={!code}
            className="inline-flex items-center gap-2 border border-napoli-gold/50 text-napoli-espresso px-5 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm transition-colors disabled:opacity-50"
          >
            <Share2 size={14} />
            Share
          </button>
          <a
            data-testid="referrals-share-whatsapp"
            href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 border border-napoli-gold/50 text-napoli-espresso px-5 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm transition-colors"
          >
            <MessageCircle size={14} />
            WhatsApp
          </a>
          <a
            data-testid="referrals-share-email"
            href={`mailto:?subject=${encodeURIComponent("Pull up a chair at Napoli")}&body=${encodeURIComponent(shareText)}`}
            className="inline-flex items-center gap-2 border border-napoli-gold/50 text-napoli-espresso px-5 py-3 font-body font-semibold text-[12px] tracking-[0.22em] uppercase hover:bg-napoli-cream-warm transition-colors"
          >
            <Mail size={14} />
            Email
          </a>
        </div>
      </Card>

      {/* Stats */}
      <Card testId="referrals-stats" className="mt-5 flex items-center justify-between flex-wrap gap-4">
        <div>
          <p className="font-body text-[10px] tracking-[0.3em] uppercase text-napoli-espresso-soft">
            Friends joined
          </p>
          <p className="mt-1 font-display font-bold text-[28px] text-napoli-oxblood">
            {successful}
          </p>
        </div>
        <div>
          <p className="font-body text-[10px] tracking-[0.3em] uppercase text-napoli-espresso-soft">
            Bonus points earned
          </p>
          <p className="mt-1 font-display font-bold text-[28px] text-napoli-gold">
            {bonus.toLocaleString()}
          </p>
        </div>
      </Card>

      {/* Recent referrals */}
      <Card testId="referrals-list" className="mt-5">
        <p className="font-body font-semibold text-[10px] tracking-[0.3em] uppercase text-napoli-oxblood">
          Recent referrals
        </p>
        {referrals.length === 0 ? (
          <div className="mt-4 text-center py-6 border border-dashed border-napoli-gold/30 bg-napoli-cream-warm/40">
            <p className="font-body italic text-[13px] text-napoli-espresso-soft">
              No referrals yet — share your code to earn points.
            </p>
          </div>
        ) : (
          <ul className="mt-4 divide-y divide-napoli-gold/15" data-testid="referrals-list-rows">
            {referrals.map((r) => (
              <li
                key={r.id}
                className="py-3 flex items-center justify-between gap-3"
              >
                <span className="font-body text-[14px] text-napoli-espresso truncate">
                  {r.first_name} {r.last_name}
                </span>
                <span
                  className={[
                    "font-body text-[11px] tracking-[0.18em] uppercase",
                    r.status === "active" ? "text-napoli-gold" : "text-napoli-espresso-soft",
                  ].join(" ")}
                >
                  {r.status}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
};

export default CustomerReferralsPage;
